import { z } from 'zod';

import {
  HoldingUnitAdjustmentTypeSchema,
  HoldingUnitIdentifierSchema,
  HoldingUnitLoadSessionItemSchema,
  HoldingUnitLoadSessionSchema,
  HoldingUnitLoadSessionMaterialGroupSchema,
  HoldingUnitLoadSessionMatchCandidateSchema,
  HoldingUnitLoadSessionStatusSchema,
  HoldingUnitSnapshotSchema,
  HoldingUnitWeightInputModeSchema,
} from './holding-units.schema.js';
import { FixedIdAssetSnapshotSchema } from './fixed-id-assets.schema.js';
import type {
  AddHoldingUnitLoadSessionItemByMatchRequest,
  AddHoldingUnitLoadSessionItemByMatchResponse,
  AddHoldingUnitLoadSessionItemsByUnitIdsRequest,
  AddHoldingUnitLoadSessionItemsByUnitIdsResponse,
  AddHoldingUnitLoadSessionItemRequest,
  AddHoldingUnitLoadSessionItemResponse,
  AddHoldingUnitWeightRequest,
  AddHoldingUnitWeightResponse,
  ApplyHoldingUnitAdjustmentRequest,
  ApplyHoldingUnitAdjustmentResponse,
  AssignHoldingUnitIdentifierRequest,
  AssignHoldingUnitIdentifierResponse,
  BuildHoldingUnitBaleRequest,
  BuildHoldingUnitBaleResponse,
  BulkTransferHoldingUnitNetRequest,
  BulkTransferHoldingUnitNetResponse,
  CorrectHoldingUnitContainerIdentityRequest,
  CorrectHoldingUnitContainerIdentityResponse,
  CorrectHoldingUnitMaterialRequest,
  CorrectHoldingUnitMaterialResponse,
  CommitHoldingUnitLoadSessionMeasuredDiscrepanciesRequest,
  CommitHoldingUnitLoadSessionMeasuredDiscrepanciesResponse,
  CorrectHoldingUnitWeightRequest,
  CorrectHoldingUnitWeightResponse,
  CreateHoldingUnitLoadSessionMaterialGroupRequest,
  CreateHoldingUnitLoadSessionMaterialGroupResponse,
  CreateHoldingUnitLoadSessionRequest,
  CreateHoldingUnitLoadSessionResponse,
  CreateHoldingUnitRequest,
  CreateHoldingUnitResponse,
  HoldingUnitWeightCompletionOutcome,
  MarkHoldingUnitsShippedRequest,
  MarkHoldingUnitsShippedResponse,
  MarkHoldingUnitReadyRequest,
  MarkHoldingUnitReadyResponse,
  MarkHoldingUnitOpenRequest,
  MarkHoldingUnitOpenResponse,
  MarkHoldingUnitReadyAndLoadRequest,
  MarkHoldingUnitReadyAndLoadResponse,
  MoveHoldingUnitYardLocationRequest,
  MoveHoldingUnitYardLocationResponse,
  RemoveHoldingUnitLoadSessionItemByIdRequest,
  RemoveHoldingUnitLoadSessionItemByIdResponse,
  RemoveHoldingUnitLoadSessionItemsByIdsRequest,
  RemoveHoldingUnitLoadSessionItemsByIdsResponse,
  DiscardHoldingUnitLoadSessionMeasuredDiscrepanciesRequest,
  DiscardHoldingUnitLoadSessionMeasuredDiscrepanciesResponse,
  RetireHoldingUnitRequest,
  RetireHoldingUnitResponse,
  SetHoldingUnitMaterialRequest,
  SetHoldingUnitMaterialResponse,
  SubmitHoldingUnitFastWeightRequest,
  SubmitHoldingUnitFastWeightResponse,
  TransferHoldingUnitNetRequest,
  TransferHoldingUnitNetResponse,
  UpdateHoldingUnitLoadSessionNameRequest,
  UpdateHoldingUnitLoadSessionNameResponse,
  UpdateHoldingUnitLoadSessionStatusRequest,
  UpdateHoldingUnitLoadSessionStatusResponse,
  UpdateHoldingUnitLoadSessionMaterialGroupRequest,
  UpdateHoldingUnitLoadSessionMaterialGroupResponse,
  UpdateHoldingUnitTareSuggestedRequest,
  UpdateHoldingUnitTareSuggestedResponse,
} from './holding-units-commands.dto.js';

const timestampSchema = z.iso.datetime();

const HoldingUnitCommandContextBaseSchema = z.object({
  companyId: z.uuid(),
  storeId: z.uuid(),
  locationId: z.uuid(),
  stationId: z.uuid(),
  occurredAt: timestampSchema.optional(),
});

const HoldingUnitCommandContextSchema = HoldingUnitCommandContextBaseSchema.extend({
  expectedVersion: z.number().int().nonnegative(),
});

const HoldingUnitUnitCommandContextSchema = HoldingUnitCommandContextSchema.extend({
  unitId: z.uuid(),
});

const OptionalHoldingUnitReasonSchema = z.string().trim().max(500).nullable().optional();

const HoldingUnitResponseSchema = z.object({
  unit: HoldingUnitSnapshotSchema,
});

const BulkTransferHoldingUnitNetExpectedVersionSchema = z.object({
  unitId: z.uuid(),
  expectedVersion: z.number().int().nonnegative(),
});

function findDuplicate(values: ReadonlyArray<string>): string | null {
  const seen = new Set<string>();
  for (const value of values) {
    if (seen.has(value)) {
      return value;
    }
    seen.add(value);
  }
  return null;
}

export const CreateHoldingUnitRequestSchema: z.ZodType<CreateHoldingUnitRequest> =
  HoldingUnitCommandContextSchema.extend({
    unitProfileId: z.uuid(),
  }).superRefine((value, ctx) => {
    if (value.expectedVersion !== 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'expectedVersion must be 0 when creating a holding unit.',
        path: ['expectedVersion'],
      });
    }
  });

export const CreateHoldingUnitResponseSchema: z.ZodType<CreateHoldingUnitResponse> =
  HoldingUnitResponseSchema;

export const SetHoldingUnitMaterialRequestSchema: z.ZodType<SetHoldingUnitMaterialRequest> =
  HoldingUnitUnitCommandContextSchema.extend({
    materialId: z.uuid(),
  });

export const SetHoldingUnitMaterialResponseSchema: z.ZodType<SetHoldingUnitMaterialResponse> =
  HoldingUnitResponseSchema;

export const CorrectHoldingUnitMaterialRequestSchema: z.ZodType<CorrectHoldingUnitMaterialRequest> =
  HoldingUnitUnitCommandContextSchema.extend({
    materialId: z.uuid(),
    reason: OptionalHoldingUnitReasonSchema,
  });

export const CorrectHoldingUnitMaterialResponseSchema: z.ZodType<CorrectHoldingUnitMaterialResponse> =
  HoldingUnitResponseSchema;

export const AssignHoldingUnitIdentifierRequestSchema: z.ZodType<AssignHoldingUnitIdentifierRequest> =
  HoldingUnitUnitCommandContextSchema.extend({
    identifier: HoldingUnitIdentifierSchema,
  }).superRefine((value, ctx) => {
    if (value.identifier.type === 'fixed') {
      ctx.addIssue({
        code: 'custom',
        message: 'Fixed identifiers are assigned through the fixed-id asset registry.',
        path: ['identifier', 'type'],
      });
    }
  });

export const AssignHoldingUnitIdentifierResponseSchema: z.ZodType<AssignHoldingUnitIdentifierResponse> =
  HoldingUnitResponseSchema;

export const MoveHoldingUnitYardLocationRequestSchema: z.ZodType<MoveHoldingUnitYardLocationRequest> =
  HoldingUnitUnitCommandContextSchema.extend({
    yardLocationId: z.uuid().nullable(),
  }).strict();

export const MoveHoldingUnitYardLocationResponseSchema: z.ZodType<MoveHoldingUnitYardLocationResponse> =
  HoldingUnitResponseSchema;

export const SubmitHoldingUnitFastWeightRequestSchema: z.ZodType<SubmitHoldingUnitFastWeightRequest> =
  HoldingUnitCommandContextSchema.extend({
    unitProfileId: z.uuid(),
    assetId: z.uuid().optional(),
    expectedAssetVersion: z.number().int().nonnegative().optional(),
    materialId: z.uuid(),
    weightInputMode: HoldingUnitWeightInputModeSchema,
    grossTotalLb: z.number().positive().optional(),
    tareLb: z.number().nonnegative().optional(),
    deltaNetLb: z.number().positive().optional(),
    isManualNetDelta: z.boolean().optional(),
    yardLocationId: z.uuid().nullable().optional(),
    markReady: z.boolean().optional(),
  })
    .strict()
    .superRefine((value, ctx) => {
      if (value.expectedVersion !== 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'expectedVersion must be 0 for Fast Weight create submit.',
          path: ['expectedVersion'],
        });
      }

      if (value.assetId && value.expectedAssetVersion === undefined) {
        ctx.addIssue({
          code: 'custom',
          message: 'expectedAssetVersion is required when starting a fixed-id asset.',
          path: ['expectedAssetVersion'],
        });
      }
      if (!value.assetId && value.expectedAssetVersion !== undefined) {
        ctx.addIssue({
          code: 'custom',
          message: 'assetId is required when expectedAssetVersion is provided.',
          path: ['assetId'],
        });
      }

      if (value.weightInputMode === 'grossTotal') {
        if (value.grossTotalLb === undefined) {
          ctx.addIssue({
            code: 'custom',
            message: 'grossTotalLb is required for grossTotal input mode.',
            path: ['grossTotalLb'],
          });
        }
        if (value.tareLb === undefined) {
          ctx.addIssue({
            code: 'custom',
            message: 'tareLb is required for grossTotal input mode.',
            path: ['tareLb'],
          });
        }
        if (value.grossTotalLb !== undefined && value.tareLb !== undefined) {
          if (value.grossTotalLb <= value.tareLb) {
            ctx.addIssue({
              code: 'custom',
              message: 'grossTotalLb must be greater than tareLb.',
              path: ['grossTotalLb'],
            });
          }
        }
        if (value.deltaNetLb !== undefined) {
          ctx.addIssue({
            code: 'custom',
            message: 'deltaNetLb is not allowed for grossTotal input mode.',
            path: ['deltaNetLb'],
          });
        }
        if (value.isManualNetDelta) {
          ctx.addIssue({
            code: 'custom',
            message: 'isManualNetDelta must be false for grossTotal input mode.',
            path: ['isManualNetDelta'],
          });
        }
        return;
      }

      if (value.deltaNetLb === undefined) {
        ctx.addIssue({
          code: 'custom',
          message: 'deltaNetLb is required for netDelta input mode.',
          path: ['deltaNetLb'],
        });
      }
      if (value.isManualNetDelta !== true) {
        ctx.addIssue({
          code: 'custom',
          message: 'isManualNetDelta must be true for netDelta input mode.',
          path: ['isManualNetDelta'],
        });
      }
      if (value.grossTotalLb !== undefined) {
        ctx.addIssue({
          code: 'custom',
          message: 'grossTotalLb is not allowed for netDelta input mode.',
          path: ['grossTotalLb'],
        });
      }
      if (value.tareLb !== undefined) {
        ctx.addIssue({
          code: 'custom',
          message: 'tareLb is not allowed for netDelta input mode.',
          path: ['tareLb'],
        });
      }
    });

export const SubmitHoldingUnitFastWeightResponseSchema: z.ZodType<SubmitHoldingUnitFastWeightResponse> =
  z.object({
    unit: HoldingUnitSnapshotSchema,
    asset: FixedIdAssetSnapshotSchema.nullable(),
  });

export const HoldingUnitWeightCompletionOutcomeSchema: z.ZodType<HoldingUnitWeightCompletionOutcome> =
  z.enum(['keepOpen', 'markReady']);

export const AddHoldingUnitWeightRequestSchema: z.ZodType<AddHoldingUnitWeightRequest> =
  HoldingUnitUnitCommandContextSchema.extend({
    completionOutcome: HoldingUnitWeightCompletionOutcomeSchema,
    inputMode: HoldingUnitWeightInputModeSchema,
    grossTotalLb: z.number().positive().optional(),
    tareLb: z.number().nonnegative().optional(),
    deltaNetLb: z.number().positive().optional(),
    isManualNetDelta: z.boolean().optional(),
  }).superRefine((value, ctx) => {
    if (value.inputMode === 'grossTotal') {
      if (value.grossTotalLb === undefined) {
        ctx.addIssue({
          code: 'custom',
          message: 'grossTotalLb is required for grossTotal input mode.',
          path: ['grossTotalLb'],
        });
      }
      if (value.tareLb === undefined) {
        ctx.addIssue({
          code: 'custom',
          message: 'tareLb is required for grossTotal input mode.',
          path: ['tareLb'],
        });
      }
      if (value.grossTotalLb !== undefined && value.tareLb !== undefined) {
        if (value.grossTotalLb <= value.tareLb) {
          ctx.addIssue({
            code: 'custom',
            message: 'grossTotalLb must be greater than tareLb.',
            path: ['grossTotalLb'],
          });
        }
      }
      if (value.deltaNetLb !== undefined) {
        ctx.addIssue({
          code: 'custom',
          message: 'deltaNetLb is not allowed for grossTotal input mode.',
          path: ['deltaNetLb'],
        });
      }
      if (value.isManualNetDelta) {
        ctx.addIssue({
          code: 'custom',
          message: 'isManualNetDelta must be false for grossTotal input mode.',
          path: ['isManualNetDelta'],
        });
      }
      return;
    }

    if (value.deltaNetLb === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'deltaNetLb is required for netDelta input mode.',
        path: ['deltaNetLb'],
      });
    }
    if (value.isManualNetDelta !== true) {
      ctx.addIssue({
        code: 'custom',
        message: 'isManualNetDelta must be true for netDelta input mode.',
        path: ['isManualNetDelta'],
      });
    }
    if (value.grossTotalLb !== undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'grossTotalLb is not allowed for netDelta input mode.',
        path: ['grossTotalLb'],
      });
    }
    if (value.tareLb !== undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'tareLb is not allowed for netDelta input mode.',
        path: ['tareLb'],
      });
    }
  });

export const AddHoldingUnitWeightResponseSchema: z.ZodType<AddHoldingUnitWeightResponse> =
  HoldingUnitResponseSchema;

export const CorrectHoldingUnitWeightRequestSchema: z.ZodType<CorrectHoldingUnitWeightRequest> =
  HoldingUnitUnitCommandContextSchema.extend({
    grossTotalLb: z.number().positive(),
    tareLb: z.number().nonnegative(),
    reason: OptionalHoldingUnitReasonSchema,
  }).superRefine((value, ctx) => {
    if (value.grossTotalLb <= value.tareLb) {
      ctx.addIssue({
        code: 'custom',
        message: 'grossTotalLb must be greater than tareLb.',
        path: ['grossTotalLb'],
      });
    }
  });

export const CorrectHoldingUnitWeightResponseSchema: z.ZodType<CorrectHoldingUnitWeightResponse> =
  HoldingUnitResponseSchema;

export const CorrectHoldingUnitContainerIdentityRequestSchema: z.ZodType<CorrectHoldingUnitContainerIdentityRequest> =
  HoldingUnitUnitCommandContextSchema.extend({
    correctedAssetId: z.uuid(),
    expectedCurrentAssetVersion: z.number().int().nonnegative(),
    expectedCorrectedAssetVersion: z.number().int().nonnegative(),
  });

export const CorrectHoldingUnitContainerIdentityResponseSchema: z.ZodType<CorrectHoldingUnitContainerIdentityResponse> =
  HoldingUnitResponseSchema;

export const UpdateHoldingUnitTareSuggestedRequestSchema: z.ZodType<UpdateHoldingUnitTareSuggestedRequest> =
  HoldingUnitUnitCommandContextSchema.extend({
    tareSuggestedLb: z.number().nonnegative().nullable(),
  });

export const UpdateHoldingUnitTareSuggestedResponseSchema: z.ZodType<UpdateHoldingUnitTareSuggestedResponse> =
  HoldingUnitResponseSchema;

export const ApplyHoldingUnitAdjustmentRequestSchema: z.ZodType<ApplyHoldingUnitAdjustmentRequest> =
  HoldingUnitUnitCommandContextSchema.extend({
    adjustmentType: HoldingUnitAdjustmentTypeSchema,
    deltaNetLb: z.number(),
    reason: z.string().trim().min(1).max(500),
  }).superRefine((value, ctx) => {
    const isShrink = value.adjustmentType === 'shrink';
    const isValid = isShrink ? value.deltaNetLb < 0 : value.deltaNetLb > 0;

    if (!isValid) {
      ctx.addIssue({
        code: 'custom',
        message: 'deltaNetLb must be negative for shrink and positive for surplus.',
        path: ['deltaNetLb'],
      });
    }
  });

export const ApplyHoldingUnitAdjustmentResponseSchema: z.ZodType<ApplyHoldingUnitAdjustmentResponse> =
  HoldingUnitResponseSchema;

export const TransferHoldingUnitNetRequestSchema: z.ZodType<TransferHoldingUnitNetRequest> =
  HoldingUnitCommandContextBaseSchema.extend({
    fromUnitId: z.uuid(),
    toUnitId: z.uuid(),
    netLb: z.number().positive(),
    fromExpectedVersion: z.number().int().nonnegative(),
    toExpectedVersion: z.number().int().nonnegative(),
  }).superRefine((value, ctx) => {
    if (value.fromUnitId === value.toUnitId) {
      ctx.addIssue({
        code: 'custom',
        message: 'fromUnitId and toUnitId must be different.',
        path: ['toUnitId'],
      });
    }
  });

export const TransferHoldingUnitNetResponseSchema: z.ZodType<TransferHoldingUnitNetResponse> =
  z.object({
    fromUnit: HoldingUnitSnapshotSchema,
    toUnit: HoldingUnitSnapshotSchema,
    materialAdopted: z.boolean(),
  });

export const BulkTransferHoldingUnitNetRequestSchema: z.ZodType<BulkTransferHoldingUnitNetRequest> =
  HoldingUnitCommandContextBaseSchema.extend({
    sourceUnitIds: z.array(z.uuid()).min(2),
    destinationUnitId: z.uuid(),
    sourceExpectedVersions: z.array(BulkTransferHoldingUnitNetExpectedVersionSchema).min(2),
    destinationExpectedVersion: z.number().int().nonnegative(),
  }).superRefine((value, ctx) => {
    const duplicateSourceId = findDuplicate(value.sourceUnitIds);
    if (duplicateSourceId) {
      ctx.addIssue({
        code: 'custom',
        message: 'sourceUnitIds must be unique.',
        path: ['sourceUnitIds'],
      });
    }

    if (value.sourceUnitIds.includes(value.destinationUnitId)) {
      ctx.addIssue({
        code: 'custom',
        message: 'destinationUnitId must be different from every sourceUnitId.',
        path: ['destinationUnitId'],
      });
    }

    const expectedUnitIds = value.sourceExpectedVersions.map((entry) => entry.unitId);
    const duplicateExpectedUnitId = findDuplicate(expectedUnitIds);
    if (duplicateExpectedUnitId) {
      ctx.addIssue({
        code: 'custom',
        message: 'sourceExpectedVersions must include each source unit once.',
        path: ['sourceExpectedVersions'],
      });
    }

    const expectedByUnitId = new Set(expectedUnitIds);
    for (const sourceUnitId of value.sourceUnitIds) {
      if (!expectedByUnitId.has(sourceUnitId)) {
        ctx.addIssue({
          code: 'custom',
          message: 'sourceExpectedVersions must include every sourceUnitId.',
          path: ['sourceExpectedVersions'],
        });
        break;
      }
    }

    const sourceUnitIds = new Set(value.sourceUnitIds);
    for (const expectedUnitId of expectedUnitIds) {
      if (!sourceUnitIds.has(expectedUnitId)) {
        ctx.addIssue({
          code: 'custom',
          message: 'sourceExpectedVersions cannot include units outside sourceUnitIds.',
          path: ['sourceExpectedVersions'],
        });
        break;
      }
    }
  });

export const BulkTransferHoldingUnitNetResponseSchema: z.ZodType<BulkTransferHoldingUnitNetResponse> =
  z.object({
    batchId: z.uuid(),
    materialId: z.uuid(),
    materialAdopted: z.boolean(),
    totalNetLb: z.number().positive(),
    transfers: z
      .array(
        z.object({
          sourceUnitId: z.uuid(),
          destinationUnitId: z.uuid(),
          netLb: z.number().positive(),
        }),
      )
      .min(2),
    sourceUnits: z.array(HoldingUnitSnapshotSchema).min(2),
    destinationUnit: HoldingUnitSnapshotSchema,
  });

const BuildHoldingUnitBaleSourceUnitInputSchema = z.object({
  unitId: z.uuid(),
  expectedVersion: z.number().int().nonnegative(),
  netLb: z.number().positive().optional(),
});

const BuildHoldingUnitBaleProductInputSchema = z
  .object({
    unitProfileId: z.uuid(),
    grossTotalLb: z.number().positive(),
    tareLb: z.number().nonnegative(),
    markReady: z.boolean().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.grossTotalLb <= value.tareLb) {
      ctx.addIssue({
        code: 'custom',
        message: 'grossTotalLb must be greater than tareLb.',
        path: ['grossTotalLb'],
      });
    }
  });

const BuildHoldingUnitBaleLeftoverDispositionSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('leave_in_staging'),
  }),
  z.object({
    mode: z.literal('create_leftover_unit'),
    unitProfileId: z.uuid(),
    tareLb: z.number().nonnegative().nullable().optional(),
    markReady: z.boolean().optional(),
  }),
  z.object({
    mode: z.literal('transfer_to_unit'),
    targetUnitId: z.uuid(),
    targetExpectedVersion: z.number().int().nonnegative(),
    netLb: z.number().positive().optional(),
  }),
]);

export const BuildHoldingUnitBaleRequestSchema: z.ZodType<BuildHoldingUnitBaleRequest> =
  HoldingUnitCommandContextBaseSchema.extend({
    stagingUnitId: z.uuid(),
    stagingExpectedVersion: z.number().int().nonnegative(),
    sourceUnits: z.array(BuildHoldingUnitBaleSourceUnitInputSchema).optional(),
    products: z.array(BuildHoldingUnitBaleProductInputSchema).min(1),
    leftoverDisposition: BuildHoldingUnitBaleLeftoverDispositionSchema.optional(),
  }).superRefine((value, ctx) => {
    const sourceUnitIds = value.sourceUnits?.map((source) => source.unitId) ?? [];
    const duplicateSourceId = findDuplicate(sourceUnitIds);
    if (duplicateSourceId) {
      ctx.addIssue({
        code: 'custom',
        message: 'sourceUnits must include each source unit once.',
        path: ['sourceUnits'],
      });
    }
    if (sourceUnitIds.includes(value.stagingUnitId)) {
      ctx.addIssue({
        code: 'custom',
        message: 'stagingUnitId must be different from every source unit.',
        path: ['stagingUnitId'],
      });
    }
    const disposition = value.leftoverDisposition;
    if (disposition?.mode === 'transfer_to_unit') {
      if (disposition.targetUnitId === value.stagingUnitId) {
        ctx.addIssue({
          code: 'custom',
          message: 'leftover target must be different from stagingUnitId.',
          path: ['leftoverDisposition', 'targetUnitId'],
        });
      }
      if (sourceUnitIds.includes(disposition.targetUnitId)) {
        ctx.addIssue({
          code: 'custom',
          message: 'leftover target must be different from every source unit.',
          path: ['leftoverDisposition', 'targetUnitId'],
        });
      }
    }
  });

export const BuildHoldingUnitBaleResponseSchema: z.ZodType<BuildHoldingUnitBaleResponse> = z.object(
  {
    batchId: z.uuid(),
    materialId: z.uuid(),
    sourceUnits: z.array(HoldingUnitSnapshotSchema),
    stagingUnit: HoldingUnitSnapshotSchema,
    productUnits: z.array(HoldingUnitSnapshotSchema).min(1),
    leftoverUnit: HoldingUnitSnapshotSchema.nullable(),
    leftoverTargetUnit: HoldingUnitSnapshotSchema.nullable(),
    sourceNetLb: z.number().min(0),
    productNetLb: z.number().positive(),
    leftoverNetLb: z.number().min(0),
    remainingStagingNetLb: z.number().min(0),
  },
);

export const MarkHoldingUnitReadyRequestSchema: z.ZodType<MarkHoldingUnitReadyRequest> =
  HoldingUnitUnitCommandContextSchema;

export const MarkHoldingUnitReadyResponseSchema: z.ZodType<MarkHoldingUnitReadyResponse> =
  HoldingUnitResponseSchema;

export const MarkHoldingUnitOpenRequestSchema: z.ZodType<MarkHoldingUnitOpenRequest> =
  HoldingUnitUnitCommandContextSchema;

export const MarkHoldingUnitOpenResponseSchema: z.ZodType<MarkHoldingUnitOpenResponse> =
  HoldingUnitResponseSchema;

export const RetireHoldingUnitRequestSchema: z.ZodType<RetireHoldingUnitRequest> =
  HoldingUnitUnitCommandContextSchema.extend({
    reason: OptionalHoldingUnitReasonSchema,
  });

export const RetireHoldingUnitResponseSchema: z.ZodType<RetireHoldingUnitResponse> = z.object({
  unitId: z.uuid(),
  lifecycleStatus: z.literal('retired'),
  retiredAt: timestampSchema,
});

export const CreateHoldingUnitLoadSessionRequestSchema: z.ZodType<CreateHoldingUnitLoadSessionRequest> =
  HoldingUnitCommandContextSchema.extend({
    sessionName: z.string().trim().min(1).max(80).nullable().optional(),
    toleranceProfileKey: z.string().trim().min(1).max(50).nullable().optional(),
    initialMaterialGroup: z.object({
      materialId: z.uuid(),
      autoPickEnabled: z.boolean(),
      toleranceProfileKey: z.string().trim().min(1).max(50).nullable().optional(),
      yardLocationIds: z.array(z.uuid()).nullable().optional(),
    }),
  }).superRefine((value, ctx) => {
    if (value.expectedVersion !== 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'expectedVersion must be 0 when creating a load session.',
        path: ['expectedVersion'],
      });
    }
  });

export const CreateHoldingUnitLoadSessionResponseSchema: z.ZodType<CreateHoldingUnitLoadSessionResponse> =
  z.object({
    loadSession: HoldingUnitLoadSessionSchema,
    group: HoldingUnitLoadSessionMaterialGroupSchema,
  });

export const UpdateHoldingUnitLoadSessionStatusRequestSchema: z.ZodType<UpdateHoldingUnitLoadSessionStatusRequest> =
  HoldingUnitCommandContextSchema.extend({
    loadSessionId: z.uuid(),
    status: HoldingUnitLoadSessionStatusSchema,
  });

export const UpdateHoldingUnitLoadSessionStatusResponseSchema: z.ZodType<UpdateHoldingUnitLoadSessionStatusResponse> =
  z.object({
    loadSession: HoldingUnitLoadSessionSchema,
  });

export const UpdateHoldingUnitLoadSessionNameRequestSchema: z.ZodType<UpdateHoldingUnitLoadSessionNameRequest> =
  HoldingUnitCommandContextSchema.extend({
    loadSessionId: z.uuid(),
    sessionName: z.string().trim().min(1).max(80).nullable(),
  });

export const UpdateHoldingUnitLoadSessionNameResponseSchema: z.ZodType<UpdateHoldingUnitLoadSessionNameResponse> =
  z.object({
    loadSession: HoldingUnitLoadSessionSchema,
  });

export const CreateHoldingUnitLoadSessionMaterialGroupRequestSchema: z.ZodType<CreateHoldingUnitLoadSessionMaterialGroupRequest> =
  HoldingUnitCommandContextSchema.extend({
    loadSessionId: z.uuid(),
    materialId: z.uuid(),
    autoPickEnabled: z.boolean(),
    toleranceProfileKey: z.string().trim().min(1).max(50).nullable().optional(),
    yardLocationIds: z.array(z.uuid()).nullable().optional(),
  });

export const CreateHoldingUnitLoadSessionMaterialGroupResponseSchema: z.ZodType<CreateHoldingUnitLoadSessionMaterialGroupResponse> =
  z.object({
    group: HoldingUnitLoadSessionMaterialGroupSchema,
  });

export const UpdateHoldingUnitLoadSessionMaterialGroupRequestSchema: z.ZodType<UpdateHoldingUnitLoadSessionMaterialGroupRequest> =
  HoldingUnitCommandContextSchema.extend({
    loadSessionId: z.uuid(),
    groupId: z.uuid(),
    autoPickEnabled: z.boolean().optional(),
    toleranceProfileKey: z.string().trim().min(1).max(50).nullable().optional(),
    yardLocationIds: z.array(z.uuid()).nullable().optional(),
  }).superRefine((value, ctx) => {
    if (
      value.autoPickEnabled === undefined &&
      value.toleranceProfileKey === undefined &&
      value.yardLocationIds === undefined
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'At least one update field is required.',
        path: ['autoPickEnabled'],
      });
    }
  });

export const UpdateHoldingUnitLoadSessionMaterialGroupResponseSchema: z.ZodType<UpdateHoldingUnitLoadSessionMaterialGroupResponse> =
  z.object({
    group: HoldingUnitLoadSessionMaterialGroupSchema,
  });

export const AddHoldingUnitLoadSessionItemRequestSchema: z.ZodType<AddHoldingUnitLoadSessionItemRequest> =
  HoldingUnitCommandContextSchema.extend({
    loadSessionId: z.uuid(),
    groupId: z.uuid(),
    unitId: z.uuid(),
    selectionMethod: z.literal('scan'),
    grossLb: z.number().positive().optional(),
    toleranceProfileKey: z.string().trim().min(1).max(50).nullable().optional(),
  })
    .loose()
    .superRefine((value, ctx) => {
      if ('tareLb' in value) {
        ctx.addIssue({
          code: 'custom',
          message: 'tareLb is not allowed. Trusted tare is read from the holding unit snapshot.',
          path: ['tareLb'],
        });
      }
    })
    .transform(
      ({
        companyId,
        storeId,
        locationId,
        stationId,
        expectedVersion,
        occurredAt,
        loadSessionId,
        groupId,
        unitId,
        selectionMethod,
        grossLb,
        toleranceProfileKey,
      }) => ({
        companyId,
        storeId,
        locationId,
        stationId,
        expectedVersion,
        occurredAt,
        loadSessionId,
        groupId,
        unitId,
        selectionMethod,
        grossLb,
        toleranceProfileKey,
      }),
    );

export const AddHoldingUnitLoadSessionItemResponseSchema: z.ZodType<AddHoldingUnitLoadSessionItemResponse> =
  z.object({
    item: HoldingUnitLoadSessionItemSchema,
  });

export const AddHoldingUnitLoadSessionItemsByUnitIdsRequestSchema: z.ZodType<AddHoldingUnitLoadSessionItemsByUnitIdsRequest> =
  HoldingUnitCommandContextSchema.extend({
    loadSessionId: z.uuid(),
    groupId: z.uuid(),
    unitIds: z.array(z.uuid()).min(1),
  });

export const AddHoldingUnitLoadSessionItemsByUnitIdsResponseSchema: z.ZodType<AddHoldingUnitLoadSessionItemsByUnitIdsResponse> =
  z.object({
    items: z.array(HoldingUnitLoadSessionItemSchema),
    appliedCount: z.number().int().nonnegative(),
    loadSessionVersion: z.number().int().nonnegative(),
  });

export const AddHoldingUnitLoadSessionItemByMatchRequestSchema: z.ZodType<AddHoldingUnitLoadSessionItemByMatchRequest> =
  HoldingUnitCommandContextSchema.extend({
    loadSessionId: z.uuid(),
    groupId: z.uuid(),
    grossLb: z.number().positive(),
    chosenUnitId: z.uuid().nullable().optional(),
    toleranceProfileKey: z.string().trim().min(1).max(50).nullable().optional(),
  });

export const AddHoldingUnitLoadSessionItemByMatchResponseSchema: z.ZodType<AddHoldingUnitLoadSessionItemByMatchResponse> =
  z.discriminatedUnion('resultType', [
    z.object({
      resultType: z.literal('added'),
      item: HoldingUnitLoadSessionItemSchema,
      autoAdded: z.boolean(),
    }),
    z.object({
      resultType: z.literal('candidates'),
      candidates: z.array(HoldingUnitLoadSessionMatchCandidateSchema),
    }),
  ]);

export const RemoveHoldingUnitLoadSessionItemByIdRequestSchema: z.ZodType<RemoveHoldingUnitLoadSessionItemByIdRequest> =
  HoldingUnitCommandContextSchema.extend({
    loadSessionId: z.uuid(),
    groupId: z.uuid(),
    itemId: z.uuid(),
  });

export const RemoveHoldingUnitLoadSessionItemByIdResponseSchema: z.ZodType<RemoveHoldingUnitLoadSessionItemByIdResponse> =
  z.object({
    removed: z.boolean(),
    item: HoldingUnitLoadSessionItemSchema.nullable(),
  });

export const RemoveHoldingUnitLoadSessionItemsByIdsRequestSchema: z.ZodType<RemoveHoldingUnitLoadSessionItemsByIdsRequest> =
  HoldingUnitCommandContextSchema.extend({
    loadSessionId: z.uuid(),
    groupId: z.uuid(),
    itemIds: z.array(z.uuid()).min(1),
  });

export const RemoveHoldingUnitLoadSessionItemsByIdsResponseSchema: z.ZodType<RemoveHoldingUnitLoadSessionItemsByIdsResponse> =
  z.object({
    items: z.array(HoldingUnitLoadSessionItemSchema),
    removedCount: z.number().int().nonnegative(),
    loadSessionVersion: z.number().int().nonnegative(),
  });

export const CommitHoldingUnitLoadSessionMeasuredDiscrepanciesRequestSchema: z.ZodType<CommitHoldingUnitLoadSessionMeasuredDiscrepanciesRequest> =
  HoldingUnitCommandContextSchema.extend({
    loadSessionId: z.uuid(),
    groupId: z.uuid(),
    itemIds: z.array(z.uuid()).min(1),
  });

export const CommitHoldingUnitLoadSessionMeasuredDiscrepanciesResponseSchema: z.ZodType<CommitHoldingUnitLoadSessionMeasuredDiscrepanciesResponse> =
  z.object({
    resolvedItemIds: z.array(z.uuid()),
    appliedCount: z.number().int().nonnegative(),
    loadSessionVersion: z.number().int().nonnegative(),
  });

export const DiscardHoldingUnitLoadSessionMeasuredDiscrepanciesRequestSchema: z.ZodType<DiscardHoldingUnitLoadSessionMeasuredDiscrepanciesRequest> =
  HoldingUnitCommandContextSchema.extend({
    loadSessionId: z.uuid(),
    groupId: z.uuid(),
    itemIds: z.array(z.uuid()).min(1),
  });

export const DiscardHoldingUnitLoadSessionMeasuredDiscrepanciesResponseSchema: z.ZodType<DiscardHoldingUnitLoadSessionMeasuredDiscrepanciesResponse> =
  z.object({
    resolvedItemIds: z.array(z.uuid()),
    appliedCount: z.number().int().nonnegative(),
    loadSessionVersion: z.number().int().nonnegative(),
  });

export const MarkHoldingUnitReadyAndLoadRequestSchema: z.ZodType<MarkHoldingUnitReadyAndLoadRequest> =
  HoldingUnitCommandContextBaseSchema.extend({
    loadSessionId: z.uuid(),
    groupId: z.uuid(),
    unitId: z.uuid(),
    loadSessionExpectedVersion: z.number().int().nonnegative(),
    unitExpectedVersion: z.number().int().nonnegative(),
    grossLb: z.number().positive().optional(),
    toleranceProfileKey: z.string().trim().min(1).max(50).nullable().optional(),
  })
    .loose()
    .superRefine((value, ctx) => {
      if ('tareLb' in value) {
        ctx.addIssue({
          code: 'custom',
          message: 'tareLb is not allowed. Trusted tare is read from the holding unit snapshot.',
          path: ['tareLb'],
        });
      }
    })
    .transform(
      ({
        companyId,
        storeId,
        locationId,
        stationId,
        occurredAt,
        loadSessionId,
        groupId,
        unitId,
        loadSessionExpectedVersion,
        unitExpectedVersion,
        grossLb,
        toleranceProfileKey,
      }) => ({
        companyId,
        storeId,
        locationId,
        stationId,
        occurredAt,
        loadSessionId,
        groupId,
        unitId,
        loadSessionExpectedVersion,
        unitExpectedVersion,
        grossLb,
        toleranceProfileKey,
      }),
    );

export const MarkHoldingUnitReadyAndLoadResponseSchema: z.ZodType<MarkHoldingUnitReadyAndLoadResponse> =
  z.object({
    unit: HoldingUnitSnapshotSchema,
    item: HoldingUnitLoadSessionItemSchema,
    loadSessionVersion: z.number().int().nonnegative(),
  });

export const MarkHoldingUnitsShippedRequestSchema: z.ZodType<MarkHoldingUnitsShippedRequest> =
  HoldingUnitCommandContextBaseSchema.extend({
    shipmentId: z.uuid().nullable().optional(),
    coverShipmentId: z.uuid().nullable().optional(),
    loadSessionId: z.uuid(),
    groupId: z.uuid(),
  }).superRefine((value, ctx) => {
    const outboundRefs = [value.shipmentId, value.coverShipmentId].filter((item): item is string =>
      Boolean(item),
    );
    if (outboundRefs.length !== 1) {
      ctx.addIssue({
        code: 'custom',
        message: 'Exactly one outbound reference is required.',
        path: ['shipmentId'],
      });
    }
  });

export const MarkHoldingUnitsShippedResponseSchema: z.ZodType<MarkHoldingUnitsShippedResponse> = z
  .object({
    shipmentId: z.uuid().nullable().optional(),
    coverShipmentId: z.uuid().nullable().optional(),
    unitIdsMarkedShipped: z.array(z.uuid()),
    shippedAt: timestampSchema,
  })
  .superRefine((value, ctx) => {
    const outboundRefs = [value.shipmentId, value.coverShipmentId].filter((item): item is string =>
      Boolean(item),
    );
    if (outboundRefs.length !== 1) {
      ctx.addIssue({
        code: 'custom',
        message: 'Exactly one outbound reference is required.',
        path: ['shipmentId'],
      });
    }
  });
